import { useQuery } from "@tanstack/react-query";

import { GetRequestVolumeTailResponse } from "./types";
import { getSignalsQueryKeyPrefix } from "./useGetSignals";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

const DEFAULT_WINDOW_MINUTES = 30;
const DEFAULT_BUCKET_SECONDS = 30;
const REQUEST_VOLUME_TAIL_POLL_INTERVAL_MS = 5_000;

interface UseGetRequestVolumeTailArgs {
  workspaceId: number;
  windowMinutes?: number;
  bucketSeconds?: number;
  promptId?: number | null;
  promptVersionNumber?: number | null;
  directOnly?: boolean;
  enabled?: boolean;
}

export const getRequestVolumeTailQueryKey = ({
  workspaceId,
  windowMinutes = DEFAULT_WINDOW_MINUTES,
  bucketSeconds = DEFAULT_BUCKET_SECONDS,
  promptId = null,
  promptVersionNumber = null,
  directOnly = false,
}: {
  workspaceId: number;
  windowMinutes?: number;
  bucketSeconds?: number;
  promptId?: number | null;
  promptVersionNumber?: number | null;
  directOnly?: boolean;
}) =>
  [
    ...getSignalsQueryKeyPrefix(workspaceId),
    "request-volume-tail",
    windowMinutes,
    bucketSeconds,
    promptId,
    promptVersionNumber,
    directOnly,
  ] as const;

export const useGetRequestVolumeTail = ({
  workspaceId,
  windowMinutes = DEFAULT_WINDOW_MINUTES,
  bucketSeconds = DEFAULT_BUCKET_SECONDS,
  promptId = null,
  promptVersionNumber = null,
  directOnly = false,
  enabled = true,
}: UseGetRequestVolumeTailArgs) => {
  const userToken = useAuth()?.userToken ?? "";

  return useQuery({
    queryKey: getRequestVolumeTailQueryKey({
      workspaceId,
      windowMinutes,
      bucketSeconds,
      promptId,
      promptVersionNumber,
      directOnly,
    }),
    queryFn: async (): Promise<GetRequestVolumeTailResponse> => {
      const params = new URLSearchParams({
        window_minutes: String(windowMinutes),
        bucket_seconds: String(bucketSeconds),
      });
      if (promptId !== null) {
        params.set("prompt_id", String(promptId));
      }
      if (promptVersionNumber !== null) {
        params.set("prompt_version_number", String(promptVersionNumber));
      }
      if (directOnly) {
        params.set("direct_only", "true");
      }

      const response = await fetch(
        `${ENDPOINTS.signals.request_volume_tail(workspaceId)}?${params}`,
        {
          method: "GET",
          headers: jsonAuthHeaders(userToken),
        },
      );

      return handleResponse(response);
    },
    enabled: enabled && Boolean(userToken) && workspaceId > 0,
    refetchInterval: REQUEST_VOLUME_TAIL_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    keepPreviousData: true,
  });
};
